import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, styled } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { ReactComponent as DemoMap } from '../assets/DemoMap.svg';

const StyledContainer = styled(Box)(({ theme }) => ({
  fontFamily: '"Philosopher", sans-serif',
  background: 'linear-gradient(to bottom right, #b4dee2, #b4e2d7)',
  minHeight: '100vh',
  height: '12000px',
  fontSize: '1rem',
  lineHeight: 1,
  position: 'relative',
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.1rem',
    lineHeight: 1.2,
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1.4rem',
  },
}));

const Line = styled(Box)({
  width: '100%',
  height: '8px',
  position: 'fixed',
  backgroundColor: '#50b5be',
  zIndex: 101,
  top: 0,
  left: 0,
  transformOrigin: 'left center',
  transform: 'scaleX(0)',
  transition: 'transform 0.1s ease-out',
});

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: '"Days One", sans-serif',
  textTransform: 'uppercase',
  position: 'fixed',
  lineHeight: 0.9,
  left: '1.8rem',
  top: '1.8rem',
  fontSize: '1.2rem',
  zIndex: 25,
  width: '6rem',
  [theme.breakpoints.up('sm')]: {
    top: 'auto',
    left: 'auto',
    right: '2.8rem',
    bottom: '2.8rem',
    fontSize: 'clamp(3.8vw, 2rem, 4.5rem)',
    width: '12rem',
  },
  [theme.breakpoints.up('md')]: {
    width: 'auto',
    textAlign: 'center',
    right: '22%',
  },
}));

const Info = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: '4.5rem',
  left: '1.3rem',
  width: 'calc(100% - 2.6rem)',
  zIndex: 8,
  padding: '10px',
  maxHeight: '92vh',
  overflowY: 'auto',
  [theme.breakpoints.up('sm')]: {
    width: '50%',
    top: '2.5rem',
    left: '2.8rem',
  },
  [theme.breakpoints.up('md')]: {
    width: '35%',
  },
  [theme.breakpoints.up('lg')]: {
    left: '45%',
    marginLeft: '-40rem',
    maxWidth: '35rem',
  },
  [theme.breakpoints.up('xl')]: {
    left: '40%',
    marginLeft: '-45rem',
    maxWidth: '35rem',
  },
}));

const InfoLocation = styled(Typography)(({ theme }) => ({
  fontFamily: '"Days One", sans-serif',
  fontSize: 'clamp(2.5vw, 1.2rem, 3.8rem)',
  marginBottom: '1.25rem',
  display: 'inline-block',
  [theme.breakpoints.up('sm')]: {
    marginBottom: '1.5rem',
  },
}));

const InfoDays = styled(Typography)({
  display: 'inline-block',
  marginLeft: '0.4rem',
});

const InfoTable = styled(Box)(({ theme }) => ({
  marginBottom: '1.5rem',
  [theme.breakpoints.up('sm')]: {
    marginBottom: '2rem',
  },
  '& span': {
    verticalAlign: 'middle',
  },
}));

const InfoTableChild = styled(Box)({
  display: 'flex',
  '&:not(:last-child)': {
    borderBottom: '1px solid black',
  },
  '&.header': {
    fontWeight: 700,
    '& .info__table__expense': {
      textAlign: 'center',
    },
  },
  '&.info__table__total': {
    fontWeight: 700,
  },
});

const InfoTableTitle = styled(Box)({
  borderRight: '1px solid black',
  padding: '10px 15px 10px 0',
  flex: '0 0 60%',
});

const InfoTableExpense = styled(Box)({
  textAlign: 'right',
  padding: '10px 0 10px 15px',
  flex: '0 0 40%',
});

const Dollar = styled('span')({
  display: 'inline-block',
  marginRight: '0.1rem',
});

const InfoCost = styled(Typography)({
  fontSize: '120%',
});

const Disclaimer = styled(Typography)({
  margin: '1.2rem 0 0',
  display: 'block',
  fontSize: '90%',
  fontStyle: 'italic',
  color: '#777',
});

const Helper = styled(Box)(({ theme }) => ({
  position: 'fixed',
  padding: '0.75rem',
  borderRadius: '0.8rem',
  background: '#ec5532',
  color: '#fff',
  boxShadow: '0px 2px 0 #fff',
  top: '1rem',
  right: '1rem',
  zIndex: 100,
  maxWidth: '50%',
  display: 'flex',
  alignItems: 'center',
  opacity: 1,
  transition: 'opacity 0.3s ease',
  [theme.breakpoints.up('sm')]: {
    bottom: '1rem',
    left: '1rem',
    top: 'auto',
    right: 'auto',
    padding: '1.2rem 1rem 1.2rem 1.2rem',
    maxWidth: 'calc(100% - 2rem)',
  },
}));

const HelperIcon = styled('svg')({
  fill: '#fff',
  width: '18px',
  height: '18px',
  marginLeft: '0.6rem',
  fontSize: '120%',
  animation: 'bounce 1.5s infinite',
  '@keyframes bounce': {
    '0%': {
      transform: 'translateY(-5px)',
      opacity: 1,
    },
    '50%': {
      opacity: 1,
    },
    '100%': {
      transform: 'translateY(3px)',
      opacity: 0,
    },
  },
});

const MapSVG = styled(DemoMap)(({ theme }) => ({
  width: '60%',
  position: 'fixed',
  height: '58vh',
  right: '3vw',
  top: '-30vh',
  [theme.breakpoints.up('sm')]: {
    height: '220vh',
    right: '-3vw',
    top: 'auto',
    bottom: '-5vh',
    width: '75%',
  },
  [theme.breakpoints.up('md')]: {
    width: '90%',
  },
  [theme.breakpoints.up('lg')]: {
    maxHeight: '2200px',
  },
  '& .pin-base': {
    transition: '0.6s ease',
    opacity: 0,
  },
  '& .pin': {
    transition: 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
    transform: 'translateY(-12px)',
  },
  '& .path': {
    strokeDasharray: '1000',
    strokeDashoffset: '1000',
    transition: 'stroke-dashoffset 0.5s ease',
  },
}));

const StyledInfoIcon = styled(InfoIcon)({
  fontSize: '1rem',
  position: 'relative',
  opacity: 0.6,
  marginLeft: '0.2rem',
  cursor: 'pointer',
  verticalAlign: 'middle',
  '&:hover': {
    opacity: 1,
  },
});

// Global styles for keyframes
const GlobalStyles = styled('div')({
  '@global': {
    '@keyframes bounce': {
      '0%': {
        transform: 'translateY(-5px)',
        opacity: 1,
      },
      '50%': {
        opacity: 1,
      },
      '100%': {
        transform: 'translateY(3px)',
        opacity: 0,
      },
    },
  },
});

const data = [
  {
    location: "Lima",
    days: "6 days",
    accommodation: "61.92",
    transportation: "191.02",
    food: "71.08",
    experiences: "10.27",
    miscellaneous: "44.42",
    total: "378.71",
    grTotal: "378.71"
  },
  {
    location: "Paracas",
    days: "1 day",
    accommodation: "12.49",
    transportation: "0",
    food: "8.88",
    experiences: "0",
    miscellaneous: "5.00",
    bigT: { name: "Ballestas Island Tour", cost: "15.00" },
    total: "41.37",
    grTotal: "420.08"
  },
  {
    location: "Huacachina",
    days: "1 day",
    accommodation: "12.49",
    transportation: "0",
    food: "14.02",
    experiences: "0",
    miscellaneous: "7.50",
    bigT: { name: "Sand Dunes", cost: "15.00" },
    total: "49.01",
    grTotal: "469.09"
  },
  {
    location: "Nazca",
    days: "1 day (Quick stop)",
    accommodation: "0",
    transportation: "0",
    food: "8.33",
    experiences: "0",
    miscellaneous: "0",
    total: "8.33",
    grTotal: "477.42"
  },
  {
    location: "Arequipa",
    days: "2 days",
    accommodation: "41.65",
    transportation: "1.67",
    food: "45.81",
    experiences: "26.38",
    miscellaneous: "5.83",
    total: "121.34",
    grTotal: "598.76"
  },
  {
    location: "Colca Canyon",
    days: "2 days",
    accommodation: "0",
    transportation: "0",
    food: "23.05",
    experiences: "4.16",
    miscellaneous: "1.67",
    bigT: { name: "Colca Canyon Tour", cost: "29.40" },
    total: "58.28",
    grTotal: "657.04"
  },
  {
    location: "Puno",
    days: "1 day",
    accommodation: "18.05",
    transportation: "29.99",
    food: "8.88",
    experiences: "0",
    miscellaneous: "79.27",
    bigT: { name: "Puno Lake Tour", cost: "10.00" },
    total: "146.19",
    grTotal: "803.23"
  },
  {
    location: "Cusco",
    days: "8 days",
    accommodation: "172.14",
    transportation: "114.39",
    food: "82.86",
    experiences: "27.21",
    miscellaneous: "10.40",
    bigT: {
      name: "Sacred Valley + Alternative Rainbow Mountain Tour",
      cost: "150.00"
    },
    total: "557.00",
    grTotal: "1360.23"
  },
  {
    location: "Inca Trail",
    days: "4 days",
    accommodation: "0",
    transportation: "0",
    food: "12.22",
    experiences: "0",
    miscellaneous: "143.82",
    bigT: { name: "Classic Inca Trail", cost: "712.17" },
    total: "868.21",
    grTotal: "2,228.44"
  }
];

export default function HomePageTripDemo() {
  const [currentData, setCurrentData] = useState(0);
  const [helperOpacity, setHelperOpacity] = useState(1);
  
  const lineRef = useRef();
  const mapRef = useRef();

  const updateData = (index) => {
    setCurrentData(index);
  };

  const animatePath = (pathId, progress) => {
    const path = document.getElementById(pathId);
    if (path) {
      const length = path.getTotalLength();
      const offset = length - (length * progress);
      path.style.strokeDashoffset = offset;
    }
  };

  const animatePin = (pinId, show) => {
    const pinGroup = document.getElementById(pinId);
    if (pinGroup) {
      const pinBase = pinGroup.querySelector('.pin-base');
      const pin = pinGroup.querySelector('.pin');
      if (pinBase && pin) {
        pinBase.style.opacity = show ? 1 : 0;
        pin.style.transform = show ? 'translateY(0)' : 'translateY(-12px)';
      }
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const maxScroll = document.body.scrollHeight - window.innerHeight;
      const progress = Math.min(scrollY / maxScroll, 1);

      // Update line progress
      if (lineRef.current) {
        lineRef.current.style.transform = `scaleX(${progress})`;
      }

      // Helper fade out
      if (scrollY > 200) {
        setHelperOpacity(0);
      } else {
        setHelperOpacity(1);
      }

      // Animate paths and pins based on scroll position
      const scrollPoints = [
        { scroll: 0, data: 0, pin: 'Lima' },
        { scroll: 800, data: 1, pin: 'Paracas', path: 'lima-para' },
        { scroll: 2000, data: 2, pin: 'Huacachina', path: 'para-huaca' },
        { scroll: 3200, data: 3, pin: 'Nazca', path: 'huaca-nazca' },
        { scroll: 4400, data: 4, pin: 'Arequipa', path: 'nazca-areq' },
        { scroll: 5800, data: 5, pin: 'Colca', path: 'areq-colca' },
        { scroll: 7100, data: 6, pin: 'Puno', path: 'colca-puno' },
        { scroll: 8300, data: 7, pin: 'Cusco', path: 'puno-cusco' },
        { scroll: 9500, data: 8, pin: 'Inca', path: 'cusco-inca' }
      ];

      // Show Lima pin from start
      animatePin('Lima', true);

      for (let i = 0; i < scrollPoints.length; i++) {
        const point = scrollPoints[i];
        const nextPoint = scrollPoints[i + 1];
        
        if (scrollY >= point.scroll) {
          updateData(point.data);
          animatePin(point.pin, true);
          
          if (point.path && nextPoint) {
            const pathProgress = Math.min((scrollY - point.scroll) / (nextPoint.scroll - point.scroll), 1);
            animatePath(point.path, pathProgress);
            
            if (pathProgress >= 1 && nextPoint.pin) {
              animatePin(nextPoint.pin, true);
            }
          }
        }
      }
    };

    // Initialize Lima
    animatePin('Lima', true);
    updateData(0);

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <StyledContainer>
      <GlobalStyles />
      <Line ref={lineRef} />
      <Title variant="h1" component="h1">
        26 Days in Peru
      </Title>
      
      <Info>
        <InfoLocation variant="h2" component="h2">
          {data[currentData].location}
        </InfoLocation>
        <InfoDays>{data[currentData].days}</InfoDays>
        
        <InfoTable>
          <InfoTableChild className="header">
            <InfoTableTitle>Item</InfoTableTitle>
            <InfoTableExpense>Cost</InfoTableExpense>
          </InfoTableChild>
          
          <InfoTableChild>
            <InfoTableTitle>Accommodation</InfoTableTitle>
            <InfoTableExpense>
              <Dollar>$</Dollar>
              <span>{data[currentData].accommodation}</span>
            </InfoTableExpense>
          </InfoTableChild>
          
          <InfoTableChild>
            <InfoTableTitle>Transportation</InfoTableTitle>
            <InfoTableExpense>
              <Dollar>$</Dollar>
              <span>{data[currentData].transportation}</span>
            </InfoTableExpense>
          </InfoTableChild>
          
          <InfoTableChild>
            <InfoTableTitle>Food/Snacks</InfoTableTitle>
            <InfoTableExpense>
              <Dollar>$</Dollar>
              <span>{data[currentData].food}</span>
            </InfoTableExpense>
          </InfoTableChild>
          
          <InfoTableChild>
            <InfoTableTitle>
              Experiences
              <StyledInfoIcon titleAccess="Mini tours, admission fees, etc." />
            </InfoTableTitle>
            <InfoTableExpense>
              <Dollar>$</Dollar>
              <span>{data[currentData].experiences}</span>
            </InfoTableExpense>
          </InfoTableChild>
          
          <InfoTableChild>
            <InfoTableTitle>
              Miscellaneous
              <StyledInfoIcon titleAccess="Tips, medication, gear rental, souvenirs, etc." />
            </InfoTableTitle>
            <InfoTableExpense>
              <Dollar>$</Dollar>
              <span>{data[currentData].miscellaneous}</span>
            </InfoTableExpense>
          </InfoTableChild>

          {data[currentData].bigT && (
            <InfoTableChild>
              <InfoTableTitle>{data[currentData].bigT.name}</InfoTableTitle>
              <InfoTableExpense>
                <Dollar>$</Dollar>
                <span>{data[currentData].bigT.cost}</span>
              </InfoTableExpense>
            </InfoTableChild>
          )}
          
          <InfoTableChild className="info__table__total">
            <InfoTableTitle>
              <strong>Total</strong>
            </InfoTableTitle>
            <InfoTableExpense>
              <strong>
                <Dollar>$</Dollar>
                <span>{data[currentData].total}</span>
              </strong>
            </InfoTableExpense>
          </InfoTableChild>
        </InfoTable>

        <Typography variant="h4" component="h4">
          Cumulative Total:
        </Typography>
        <InfoCost>
          <Dollar>$</Dollar>
          <span>{data[currentData].grTotal}</span>
        </InfoCost>
        
        <Disclaimer>All costs in USD</Disclaimer>
      </Info>

      <Helper style={{ opacity: helperOpacity }}>
        Keep scrolling to see more data
        <HelperIcon xmlns="http://www.w3.org/2000/svg" viewBox="0 0 436.717 436.717">
          <path d="M427.758,194.331c11.945,11.94,11.945,31.298,0,43.243L239.979,425.359c-5.97,5.958-13.796,8.949-21.622,8.949   s-15.651-2.979-21.619-8.949L8.959,237.574c-11.946-11.945-11.946-31.303,0-43.243c11.943-11.942,31.306-11.942,43.246,0   l166.151,166.152l166.152-166.152C396.448,182.395,415.818,182.395,427.758,194.331z M196.738,242.397   c5.967,5.958,13.793,8.949,21.619,8.949s15.652-2.991,21.622-8.949L427.758,54.618c11.945-11.948,11.945-31.312,0-43.255   c-11.945-11.939-31.31-11.939-43.249,0L218.357,177.515L52.206,11.363c-11.94-11.939-31.303-11.939-43.246,0   c-11.946,11.943-11.946,31.307,0,43.255L196.738,242.397z" />
        </HelperIcon>
      </Helper>

      <MapSVG ref={mapRef} />
    </StyledContainer>
  );
}

